// The FreightSimple loading spinner — the single spinner used everywhere
// (page/data loading, buttons, inline field loading). A faithful SVG port of
// the dashboard's Lottie loader: a blue and a gold dot slide horizontally and
// swap sides (an orbit seen edge-on — the xz plane), the gold dot passing in
// front. Self-contained SMIL, no Lottie asset or animation library.
//
// Geometry/timing come straight from progress-animation.json (300px canvas,
// ~41-frame @30fps cycle, dots ~41px travelling x 100↔180 at y 156.5), scaled
// into a 24-unit viewBox.

interface SpinnerProps {
  /** Diameter in pixels. Default 32. */
  size?: number;
  /**
   * Set on a filled/colored surface (e.g. a primary button, the chat send
   * button). Swaps the blue dot for the current text color (white on a blue/
   * dark button) so it stays visible where blue would vanish into a blue
   * background; the gold dot keeps its brand colour. On light surfaces leave
   * this off for the full blue+gold brand look.
   */
  onColor?: boolean;
}

const CYCLE_SECONDS = 1.35;
// Ease in/out at each end of the slide, matching the Lottie's bezier handles.
const EASE = "0.45 0 0.55 1;0.45 0 0.55 1";

export function Spinner({ size = 32, onColor = false }: SpinnerProps) {
  const leadClass = onColor ? "fill-current" : "fill-[var(--color-blue)]";
  const trailClass = "fill-[var(--color-gold)]";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="status"
      aria-label="Loading"
    >
      {/* Blue starts on the right and slides left; gold starts on the left and
          slides right, drawn second so it passes in front at the crossing. */}
      <circle cy="12" r="3" className={leadClass}>
        <animate
          attributeName="cx"
          dur={`${CYCLE_SECONDS}s`}
          repeatCount="indefinite"
          calcMode="spline"
          keyTimes="0;0.5;1"
          values="17;7;17"
          keySplines={EASE}
        />
      </circle>
      <circle cy="12" r="3" className={trailClass}>
        <animate
          attributeName="cx"
          dur={`${CYCLE_SECONDS}s`}
          repeatCount="indefinite"
          calcMode="spline"
          keyTimes="0;0.5;1"
          values="7;17;7"
          keySplines={EASE}
        />
      </circle>
    </svg>
  );
}
